import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';

interface TemplateRow {
  id: string;
  customer_email: string;
  customer_company: string;
  name: string;
  template_type: string;
  content: string;
  variables: string[];
  is_active: boolean;
  usage_count: number;
  created_at: string;
}

const repo = new AdminListRepository<TemplateRow>(apiClient, 'templates');

const columns: DataTableColumn<TemplateRow>[] = [
  { id: 'name', header: 'Name', accessor: 'name' },
  {
    id: 'customer_email', header: 'Customer',
    accessor: (r) => (
      <span>
        {r.customer_company}
        <span className="block text-xs text-slate-500">{r.customer_email}</span>
      </span>
    ),
  },
  { id: 'template_type', header: 'Type', accessor: 'template_type' },
  {
    id: 'content',
    header: 'Content (preview)',
    accessor: (r) => (
      <span className="block max-w-xs truncate text-xs text-slate-600" title={r.content}>
        {r.content}
      </span>
    ),
  },
  {
    id: 'variables',
    header: 'Variables',
    accessor: (r) => (r.variables ?? []).join(', ') || '—',
  },
  { id: 'usage_count', header: 'Campaigns', accessor: 'usage_count' },
  { id: 'is_active', header: 'Active', accessor: (r) => (r.is_active ? 'Yes' : 'No') },
];

export function TemplatesPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (typeFilter) f.type = typeFilter;
    return f;
  }, [search, typeFilter]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">Message Templates</h2>
        <div className="flex flex-wrap gap-2 text-sm">
        <input
          type="search"
          placeholder="Search name or customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border px-2 py-1"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded border px-2 py-1"
        >
          <option value="">All types</option>
          <option value="otp">OTP</option>
          <option value="welcome">Welcome</option>
          <option value="notification">Notification</option>
          <option value="reminder">Reminder</option>
          <option value="promotional">Promotional</option>
          <option value="custom">Custom</option>
        </select>
        </div>
      </div>
      <DataTable<TemplateRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        emptyState={<span data-testid="datatable-empty">No templates yet.</span>}
      />
    </section>
  );
}
